// src/utils/money.model.ts
var MONEY_DECIMALS = 1;
var ROUND_FACTOR = 10 ** MONEY_DECIMALS;
function roundMoney(value, roundAmounts = false) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const factor = roundAmounts ? 1 : ROUND_FACTOR;
  const scaled = value * factor;
  const roundedScaled = scaled >= 0 ? Math.floor(scaled + 0.5) : Math.ceil(scaled - 0.5);
  return roundedScaled / factor;
}
function splitMoney(total, count, roundAmounts = false) {
  if (!Number.isFinite(total) || !Number.isInteger(count) || count <= 0) {
    return [];
  }
  const factor = roundAmounts ? 1 : ROUND_FACTOR;
  const units = Math.round(Math.abs(roundMoney(total, roundAmounts)) * factor);
  const base = Math.floor(units / count);
  const remainder = units - base * count;
  const sign = total < 0 ? -1 : 1;
  return Array.from(
    { length: count },
    (_, index) => sign * (base + (index >= count - remainder ? 1 : 0)) / factor
  );
}
function allocateMoneyPortion(total, totalQuantity, paidQuantity, selectedQuantity, roundAmounts = false) {
  if (totalQuantity <= 0)
    return 0;
  if (!roundAmounts)
    return total * selectedQuantity / totalQuantity;
  return splitMoney(total, totalQuantity, true).slice(paidQuantity, paidQuantity + selectedQuantity).reduce((sum, amount) => sum + amount, 0);
}

// src/pos/ordering.model.ts
import { z } from "zod";
var updateRoundAmountsSchema = z.object({ roundAmounts: z.boolean().optional() }).strict();
var QUICK_CHARGE_CODE_PREFIX = "QC_";
var KIOSK_DINE_IN_CODE_PREFIX = "KI_";
function isQuickChargeOrderCode(orderCode) {
  return orderCode?.startsWith(QUICK_CHARGE_CODE_PREFIX) ?? false;
}
function isKioskDineInOrderCode(orderCode) {
  return orderCode?.startsWith(KIOSK_DINE_IN_CODE_PREFIX) ?? false;
}
function getComboMoveSource(source) {
  const components = (source.selections ?? []).filter((item) => item.groupType === "combo");
  if (components.length === 0)
    return { name: source.productName ?? "", quantity: 1, note: source.note };
  const component = components[0];
  if (components.length !== 1 || !component || component.selectionId == null || !Number.isInteger(component.quantity) || component.quantity <= 0 || component.sentQuantity == null || !Number.isInteger(component.sentQuantity) || component.sentQuantity < 0 || component.sentQuantity > component.quantity * source.quantity || source.status !== "skipped" && component.sentQuantity !== component.quantity * source.quantity)
    return null;
  return {
    name: component.itemName,
    quantity: component.quantity,
    note: component.note ?? source.note,
    sourceSelectionId: component.selectionId,
    expectedSourceSentQuantity: component.sentQuantity
  };
}
function isComboMoveNameMatch(productName, itemName) {
  const source = productName?.trim().toLowerCase();
  const target = itemName.trim().toLowerCase();
  return !!source && !!target && (source.includes(target) || target.includes(source));
}
function hasPendingComboComponents(line) {
  if (line.status !== "pending" && line.status !== "sent")
    return false;
  return (line.selections ?? []).some((item) => item.groupType === "combo" && (item.sentQuantity !== void 0 ? item.sentQuantity < item.quantity * line.quantity : line.status === "pending"));
}
function expandMergeTableIds(tableIds, links) {
  const groupOrderIds = new Set(links.filter((link) => tableIds.includes(link.tablePk)).map((link) => link.orderPk));
  return [.../* @__PURE__ */ new Set([...tableIds, ...links.filter((link) => groupOrderIds.has(link.orderPk)).map((link) => link.tablePk)])];
}

// src/pos/min-order.model.ts
var MIN_ORDER_COMPLETION_NOTE_PREFIX = "\u05D4\u05E9\u05DC\u05DE\u05D4 \u05DC\u05DE\u05D9\u05E0\u05D9\u05DE\u05D5\u05DD";
var MIN_ORDER_COMPLETION_LABEL = "\u05D4\u05E9\u05DC\u05DE\u05EA \u05DE\u05D9\u05E0\u05D9\u05DE\u05D5\u05DD \u05D4\u05D6\u05DE\u05E0\u05D4";
function isMinOrderCompletionNote(note) {
  return typeof note === "string" && note.startsWith(MIN_ORDER_COMPLETION_NOTE_PREFIX);
}
function resolveEffectiveMinimumOrder(config, dinersCount) {
  if (config.minimumOrderPerDiner > 0) {
    const diners = Number.isFinite(dinersCount) && dinersCount > 0 ? Math.floor(dinersCount) : 0;
    return roundMoney(config.minimumOrderPerDiner * diners);
  }
  return config.minimumOrder > 0 ? roundMoney(config.minimumOrder) : 0;
}

// src/pos/cash-alterations.model.ts
import { z as z2 } from "zod";
var CASH_ALTERATION_ACTIONS = ["entry-cash", "in", "out"];
var cashAlterationActionSchema = z2.enum(CASH_ALTERATION_ACTIONS);

// src/pos/credit-payments.model.ts
var FAILED_CREDIT_RECOVERY_DELAY_MS = 9e3;

// src/pos/receipt-pdf.util.ts
var PAPER_WIDTH_MM = 82;
var PT_PER_MM = 72 / 25.4;
var asBytes = (text) => {
  const bytes = new Uint8Array(text.length);
  for (let index = 0; index < text.length; index += 1) {
    bytes[index] = text.charCodeAt(index) & 255;
  }
  return bytes;
};
var buildJpegPdf = (jpeg, pixelWidth, pixelHeight) => {
  const widthPt = Math.round(PAPER_WIDTH_MM * PT_PER_MM * 100) / 100;
  const heightPt = Math.round(widthPt * (pixelHeight / pixelWidth) * 100) / 100;
  const content = `q
${widthPt} 0 0 ${heightPt} 0 0 cm
/Im0 Do
Q
`;
  const chunks = [];
  const offsets = [];
  let length = 0;
  const push = (part) => {
    const bytes = typeof part === "string" ? asBytes(part) : part;
    chunks.push(bytes);
    length += bytes.length;
  };
  const startObject = (id) => {
    offsets[id] = length;
    push(`${id} 0 obj
`);
  };
  push("%PDF-1.4\n%\xE2\xE3\xCF\xD3\n");
  startObject(1);
  push("<< /Type /Catalog /Pages 2 0 R >>\nendobj\n");
  startObject(2);
  push("<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n");
  startObject(3);
  push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${widthPt} ${heightPt}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>
endobj
`);
  startObject(4);
  push(`<< /Type /XObject /Subtype /Image /Width ${pixelWidth} /Height ${pixelHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>
stream
`);
  push(jpeg);
  push("\nendstream\nendobj\n");
  startObject(5);
  push(`<< /Length ${content.length} >>
stream
${content}endstream
endobj
`);
  const xrefOffset = length;
  push("xref\n0 6\n");
  push("0000000000 65535 f \n");
  for (let id = 1; id <= 5; id += 1) {
    push(`${String(offsets[id]).padStart(10, "0")} 00000 n 
`);
  }
  push(`trailer
<< /Size 6 /Root 1 0 R >>
startxref
${xrefOffset}
%%EOF
`);
  const pdf = new Uint8Array(length);
  let cursor = 0;
  for (const chunk of chunks) {
    pdf.set(chunk, cursor);
    cursor += chunk.length;
  }
  return pdf;
};
var readJpegSize = (jpeg) => {
  let offset = 2;
  while (offset + 9 < jpeg.length) {
    if (jpeg[offset] !== 255) {
      offset += 1;
      continue;
    }
    const marker = jpeg[offset + 1];
    if (marker >= 192 && marker <= 207 && marker !== 196 && marker !== 200 && marker !== 204) {
      return {
        height: jpeg[offset + 5] << 8 | jpeg[offset + 6],
        width: jpeg[offset + 7] << 8 | jpeg[offset + 8]
      };
    }
    offset += 2 + (jpeg[offset + 2] << 8 | jpeg[offset + 3]);
  }
  return null;
};

// src/pos/tips.model.ts
function allocatePaymentlineTips(globalTip, collectedTip, lines, roundAmounts = false) {
  const pinned = lines.filter((line) => line.globalTipPinned);
  const open = lines.filter((line) => !line.globalTipPinned);
  const reservedTip = pinned.reduce((sum, line) => sum + (line.globalTipCollected ?? 0), 0);
  const amounts = open.map((line) => line.amount + (roundAmounts ? resolveTip(line.tipInputMode, line.tipInputValue, line.amount, true) : 0));
  const shares = allocateGlobalTip(roundMoney(globalTip - collectedTip - reservedTip, roundAmounts), amounts, true, roundAmounts);
  const result = new Map(open.map((line, index) => [line.id, shares[index] ?? 0]));
  pinned.forEach((line) => result.set(line.id, line.globalTipCollected ?? 0));
  return result;
}
function resolveTip(mode, value, base, roundAmounts = false) {
  if (mode == null || value == null) {
    return 0;
  }
  if (mode === "value") {
    return roundMoney(value, roundAmounts);
  }
  if (value <= 0) {
    return 0;
  }
  return roundMoney(base * value / 100, roundAmounts);
}
function allocateGlobalTipShare(remainingTip, lineAmount, remainingItems, isClosingLine, roundAmounts = false) {
  if (remainingTip <= 0) {
    return 0;
  }
  if (isClosingLine) {
    return roundMoney(Math.max(0, remainingTip), roundAmounts);
  }
  if (remainingItems <= 0) {
    return 0;
  }
  return Math.min(remainingTip, roundMoney(remainingTip * lineAmount / remainingItems, roundAmounts));
}
function allocateProportionalTip(total, lineAmounts, roundAmounts = false) {
  let remainingTip = total;
  let remainingItems = roundMoney(lineAmounts.reduce((sum, amount) => sum + amount, 0));
  return lineAmounts.map((amount, index) => {
    const isClosingLine = index === lineAmounts.length - 1;
    const share = allocateGlobalTipShare(remainingTip, amount, remainingItems, isClosingLine, roundAmounts);
    remainingTip = roundMoney(remainingTip - share);
    remainingItems = roundMoney(remainingItems - amount);
    return share;
  });
}
function isEqualSplit(lineAmounts, roundAmounts = false) {
  if (lineAmounts.length < 2) {
    return true;
  }
  return roundMoney(Math.max(...lineAmounts) - Math.min(...lineAmounts)) <= (roundAmounts ? 1 : 0.1);
}
function allocateEvenTotalTip(total, lineAmounts, itemsTotal, roundAmounts = false) {
  if (roundAmounts) {
    const targets = splitMoney(itemsTotal + total, lineAmounts.length, true);
    const shares2 = targets.map((target, index) => target - (lineAmounts[index] ?? 0));
    return shares2.some((share) => share < 0) ? null : shares2;
  }
  const n = lineAmounts.length;
  const grandTenths = Math.round(roundMoney(itemsTotal + total) * 10);
  const baseTenths = Math.floor(grandTenths / n);
  const extra = grandTenths - baseTenths * n;
  const shares = lineAmounts.map((amount, index) => {
    const targetTenths = baseTenths + (index >= n - extra ? 1 : 0);
    return roundMoney(targetTenths / 10 - amount);
  });
  return shares.some((share) => share < 0) ? null : shares;
}
function allocateGlobalTip(globalTip, lineAmounts, equalShares = false, roundAmounts = false) {
  const total = roundMoney(globalTip, roundAmounts);
  const itemsTotal = roundMoney(lineAmounts.reduce((sum, amount) => sum + amount, 0));
  const allZeroLines = lineAmounts.length > 0 && lineAmounts.every((amount) => roundMoney(amount) === 0);
  if (total <= 0 || itemsTotal <= 0 && !allZeroLines) {
    return lineAmounts.map(() => 0);
  }
  if (roundAmounts && isEqualSplit(lineAmounts, true)) {
    const evenTotal = allocateEvenTotalTip(total, lineAmounts, itemsTotal, true);
    if (evenTotal)
      return evenTotal;
  }
  if (equalShares) {
    return allocateEvenTotalTip(total, lineAmounts.map(() => 0), 0, roundAmounts) ?? [];
  }
  if (isEqualSplit(lineAmounts, roundAmounts)) {
    const evenTotal = allocateEvenTotalTip(total, lineAmounts, itemsTotal, roundAmounts);
    if (evenTotal) {
      return evenTotal;
    }
  }
  return allocateProportionalTip(total, lineAmounts, roundAmounts);
}

// src/pos/workforce-printing.model.ts
function getPrinterDisplayName(printer) {
  return printer.customName?.trim() || printer.name;
}

// src/pos/area-print-rules.model.ts
import { z as z3 } from "zod";
var areaPrintRulePrinterMappingSchema = z3.object({
  printerId: z3.number().int().positive(),
  areaIds: z3.array(z3.number().int().positive())
}).strict();
var areaPrintRuleSchema = z3.object({
  id: z3.string().trim().min(1).max(120),
  name: z3.string().trim().min(1).max(120),
  productIds: z3.array(z3.number().int().positive()),
  printerMappings: z3.array(areaPrintRulePrinterMappingSchema).min(2)
}).strict();
var updateAreaPrintRulesSchema = z3.object({
  areaPrintRules: z3.array(areaPrintRuleSchema)
}).strict();

// src/pos/stream-events.model.ts
var POS_STREAM_EVENT_TYPES = [
  "accountant_report.delivery_updated",
  "tax.connection.updated",
  "tax.invoice.updated",
  "order.opened",
  "order.upserted",
  "order.closed",
  "orderline.added",
  "orderline.updated",
  "orderline.item_payment_updated",
  "orderline.removed",
  "orderline.sent",
  "paymentline.created",
  "paymentline.done",
  "credit.tap.pending",
  "credit.tap.released",
  "credit.transaction.pending",
  "credit.transaction.resolved",
  "session.opened",
  "session.closed",
  "session.cash_alterations_updated",
  "cashDrawer.notConfigured",
  "session.daily_brief_updated",
  "session.auto_close_time_updated",
  "tenant.floor_background_updated",
  "tenant.general_item_requires_manager_approval_updated",
  "tenant.action_requires_manager_approval_updated",
  "tenant.operations_approval_requirement_updated",
  "tenant.orders_page_waiter_own_visibility_updated",
  "tenant.manual_credit_entry_blocked_updated",
  "tenant.large_table_number_on_receipt_updated",
  "tenant.skip_take_away_receipt_print_updated",
  "tenant.require_employee_report_before_clock_out_updated",
  "tenant.hide_cash_out_updated",
  "tenant.stationary_name_updated",
  "tenant.z_report_email_recipients_updated",
  "tenant.exclude_opening_cash_from_drawer_updated",
  "tenant.round_amounts_updated",
  "tenant.open_drawer_on_partial_cash_updated",
  "tenant.open_drawer_employee_ids_updated",
  "tenant.open_drawer_requires_password_updated",
  "tenant.refund_code_via_push_updated",
  "tenant.employee_attendance_report_enabled_updated",
  "tenant.attendance_report_rules_updated",
  "area.upserted",
  "table.upserted",
  "table.removed",
  "employee.upserted",
  "employee.deactivated",
  "employee.kicked",
  "product.upserted",
  "product.deactivated",
  "payment-terminal.upserted",
  "payment-terminal.active-changed",
  "category.upserted",
  "printer.upserted",
  "print_job.updated",
  "happy_hour.changed",
  "variantComboGroup.upserted",
  "variantComboGroup.removed",
  "merge.links.changed",
  "approval_request.created",
  "approval_request.decided",
  "tenant.maintenance-mode-changed",
  "pos.refresh-requested",
  "deployment.refresh-requested",
  "employee.presence.probe.requested",
  "green_fire.rescue.requested",
  "employee.presence.probe.acknowledged"
];

// src/pos/reports.model.ts
import { z as z4 } from "zod";
var zReportEmailRecipientsSchema = z4.array(z4.string().trim().max(254).email().toLowerCase()).max(20).transform((emails) => [...new Set(emails)]);
var updateZReportEmailRecipientsSchema = z4.object({
  zReportEmailRecipients: zReportEmailRecipientsSchema.optional()
}).strict();
var zReportEmailPdfSchema = z4.string().max(7e6).startsWith("JVBERi0").regex(/^[A-Za-z0-9+/]+={0,2}$/);
var sendZReportEmailSchema = z4.object({
  pdfBase64: zReportEmailPdfSchema.optional(),
  printableImageDataUrl: z4.string().max(75e5).regex(/^data:image\/jpeg;base64,[A-Za-z0-9+/]+={0,2}$/).optional()
}).strict().refine((value) => Boolean(value.pdfBase64 || value.printableImageDataUrl), "Z report image or PDF required");

// src/pos/past-receipts.model.ts
import { z as z5 } from "zod";
var CLOSED_ORDERS_PAGE_SIZE_MAX = 50;
var CLOSED_ORDERS_PAGE_SIZE_DEFAULT = 30;
var SESSION_FILTER_OPTIONS_LIMIT = 200;
var closedOrdersQuerySchema = z5.object({
  page: z5.coerce.number().int().min(1).default(1),
  pageSize: z5.coerce.number().int().min(1).max(CLOSED_ORDERS_PAGE_SIZE_MAX).default(CLOSED_ORDERS_PAGE_SIZE_DEFAULT),
  dateFrom: z5.string().trim().min(1).optional(),
  dateTo: z5.string().trim().min(1).optional(),
  search: z5.string().trim().min(1).max(120).optional(),
  customer: z5.string().trim().min(1).max(120).optional(),
  sessionId: z5.coerce.number().int().min(1).optional()
});

// src/pos/accountant-report.model.ts
import { z as z6 } from "zod";
var accountantReportRangeSchema = z6.object({
  from: z6.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z6.string().regex(/^\d{4}-\d{2}-\d{2}$/)
}).strict().refine((value) => value.from <= value.to, { message: "from must be on or before to", path: ["from"] });
var accountantReportSessionsSchema = z6.object({
  sessionIds: z6.array(z6.number().int().positive()).min(1)
}).strict();
var accountantReportDeliveryPatchSchema = z6.object({
  recipients: z6.array(z6.string().trim().toLowerCase().email().max(254)).max(20).transform((emails) => [...new Set(emails)].sort()).optional(),
  dayOfMonth: z6.number().int().min(1).max(31).optional()
}).strict();

// src/pos/accountant-report-print.ts
var escapeHtml = (value) => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
function buildAccountantReportHtml(view, formatAmount = (value) => value.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }), formatDate = (iso) => new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Jerusalem" }).format(new Date(iso))) {
  const num = (value) => `\u2066${escapeHtml(formatAmount(value))}\u2069`;
  const paymentColSpan = 5 + view.cardBrands.length + 1;
  const brandHeadCells = view.cardBrands.map((brand) => `<th class="num">${escapeHtml(brand)}</th>`).join("");
  const bodyRows = view.rows.map((row) => `<tr>
    <td class="text">${escapeHtml(formatDate(row.date))}</td><td class="text">${escapeHtml(row.sessionName)}</td>
    <td class="num">${num(row.net)}</td><td class="num">${num(row.vat)}</td><td class="num">${num(row.gross)}</td>
    <td class="num">${num(row.cash)}</td><td class="num">${num(row.checks)}</td><td class="num">${num(row.onAccount)}</td>
    <td class="num">${num(row.bankTransfer)}</td>
    ${view.cardBrands.map((brand) => `<td class="num">${num(row.byBrand[brand] ?? 0)}</td>`).join("")}
    <td class="num">${num(row.other)}</td><td class="num">${num(row.totalIncome)}</td><td class="num">${num(row.tips)}</td>
  </tr>`).join("");
  const totals = view.totals;
  const totalsRow = `<tr class="totals"><th class="text">\u05E1\u05D4"\u05DB</th><th class="text"></th>
    <th class="num">${num(totals.net)}</th><th class="num">${num(totals.vat)}</th><th class="num">${num(totals.gross)}</th>
    <th class="num">${num(totals.cash)}</th><th class="num">${num(totals.checks)}</th><th class="num">${num(totals.onAccount)}</th>
    <th class="num">${num(totals.bankTransfer)}</th>
    ${view.cardBrands.map((brand) => `<th class="num">${num(totals.byBrand[brand] ?? 0)}</th>`).join("")}
    <th class="num">${num(totals.other)}</th><th class="num">${num(totals.totalIncome)}</th><th class="num">${num(totals.tips)}</th></tr>`;
  const title = `\u05D3\u05D5"\u05D7 \u05E8\u05D5\u05D0\u05D4 \u05D7\u05E9\u05D1\u05D5\u05DF \u2014 ${escapeHtml(view.businessName)}`;
  return `<!doctype html><html lang="he" dir="rtl"><head><meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" /><title>${title}</title>
    <style>
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #fff; color: #1f2933; font-family: 'Segoe UI', 'Noto Sans Hebrew', Arial, sans-serif; direction: rtl; }
      .host { padding: 16px; }
      h1 { font-size: 18px; font-weight: 700; margin: 0 0 12px; text-align: right; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      th, td { border: 1px solid #c7ccd1; padding: 4px 6px; white-space: nowrap; }
      thead th { background: #eef1f4; font-weight: 700; }
      th.group { text-align: center; }
      td.text, th.text { text-align: right; }
      td.num, th.num { text-align: center; font-variant-numeric: tabular-nums; }
      tbody tr:nth-child(even) td { background: #f7f8fa; }
      tfoot .totals th { background: #eef1f4; font-weight: 700; }
      td.empty { text-align: center; color: #6b7280; padding: 16px; }
      @page { size: landscape; margin: 8mm; }
      @media print { .host { padding: 0; } thead { display: table-header-group; } tfoot { display: table-footer-group; } tr { break-inside: avoid; } }
    </style></head><body><div class="host"><h1>${title}</h1><table><thead><tr>
      <th class="text" rowspan="2">\u05EA\u05D0\u05E8\u05D9\u05DA</th><th class="text" rowspan="2">\u05DE\u05E1\u05E4\u05E8 Z</th>
      <th class="group" colspan="3">\u05E1\u05DA \u05D4\u05DB\u05DC \u05DE\u05DB\u05D9\u05E8\u05D5\u05EA</th><th class="group" colspan="${paymentColSpan}">\u05E1\u05D5\u05D2\u05D9 \u05EA\u05E9\u05DC\u05D5\u05DD</th>
      <th class="num" rowspan="2">\u05E2\u05D5\u05D3\u05E3 \u05D8\u05D9\u05E4</th></tr><tr>
      <th class="num">\u05DE\u05DB\u05D9\u05E8\u05D5\u05EA \u05E0\u05D8\u05D5</th><th class="num">\u05DE\u05E2"\u05DE</th><th class="num">\u05E1\u05D4"\u05DB \u05DE\u05DB\u05D9\u05E8\u05D5\u05EA</th>
      <th class="num">\u05DE\u05D6\u05D5\u05DE\u05DF</th><th class="num">\u05D4\u05DE\u05D7\u05D0\u05D5\u05EA</th><th class="num">\u05DC\u05E7\u05D5\u05D7 \u05E7\u05D9\u05D9\u05DD</th><th class="num">\u05D4\u05E2\u05D1\u05E8\u05D4 \u05D1\u05E0\u05E7\u05D0\u05D9\u05EA</th>
      ${brandHeadCells}<th class="num">\u05D0\u05D7\u05E8</th><th class="num">\u05E1\u05D4"\u05DB \u05D4\u05DB\u05E0\u05E1\u05D5\u05EA</th></tr></thead>
      <tbody>${view.rows.length ? bodyRows : `<tr><td class="empty" colspan="${6 + paymentColSpan}">\u05D0\u05D9\u05DF \u05DE\u05E9\u05DE\u05E8\u05D5\u05EA \u05D1\u05D8\u05D5\u05D5\u05D7 \u05E9\u05E0\u05D1\u05D7\u05E8</td></tr>`}</tbody>
      ${view.rows.length ? `<tfoot>${totalsRow}</tfoot>` : ""}</table></div></body></html>`;
}

// src/pos/attendance-rules.model.ts
var DEFAULT_ATTENDANCE_REPORT_RULES = {
  overtime125StartHours: 8,
  overtime150StartHours: 10,
  useNightShiftOvertimeRules: false,
  overtime150Range: null,
  overtime150Weekdays: [],
  overtime200SpecialDates: []
};

// src/pos/assistant.model.ts
var ASSISTANT_QUESTION_MIN_LENGTH = 2;
var ASSISTANT_QUESTION_MAX_LENGTH = 500;
var ASSISTANT_FEEDBACK_MIN_LENGTH = 2;
var ASSISTANT_FEEDBACK_MAX_LENGTH = 2e3;
var ASSISTANT_HISTORY_MAX_TURNS = 6;
var ASSISTANT_HISTORY_TURN_MAX_LENGTH = 1e3;

// src/pos/z-report-layout.model.ts
import { z as z7 } from "zod";
var zReportLayoutEntryTypeSchema = z7.enum(["section", "row"]);
var zReportLayoutEntrySchema = z7.object({
  key: z7.string().trim().min(1).max(120),
  sourceKey: z7.string().trim().min(1).max(120).optional(),
  type: zReportLayoutEntryTypeSchema,
  visible: z7.boolean(),
  bold: z7.boolean().optional(),
  negative: z7.boolean().optional(),
  label: z7.string().trim().min(1).max(120)
}).strict();
var zReportLayoutConfigSchema = z7.object({
  version: z7.literal(1),
  entries: z7.array(zReportLayoutEntrySchema).max(400)
}).strict();

// src/pos/e2e-runner.model.ts
import { z as z8 } from "zod";
var E2E_RUN_TIERS = ["gate", "full"];
var E2E_RUN_BRANCH_PATTERN = /^[\w./-]+$/;
var e2eRunStartSchema = z8.object({
  tier: z8.enum(E2E_RUN_TIERS),
  branch: z8.string().min(1).max(200).regex(E2E_RUN_BRANCH_PATTERN).optional()
});

// src/pos/session-cash-count.model.ts
import { z as z9 } from "zod";
var CASH_COUNT_BILLS = [200, 100, 50, 20];
var CASH_COUNT_COINS = [10, 5, 2, 1, 0.5, 0.1];
var CASH_COUNT_DENOMINATIONS = [...CASH_COUNT_BILLS, ...CASH_COUNT_COINS];
var CASH_COUNT_MAX_QUANTITY = 99999;
var CASH_COUNT_STATION_NAME_MAX = 50;
var quantityField = z9.number().int().min(0).max(CASH_COUNT_MAX_QUANTITY).default(0);
var cashCountQuantitiesSchema = z9.object(
  Object.fromEntries(CASH_COUNT_DENOMINATIONS.map((denomination) => [String(denomination), quantityField]))
).strict();
var upsertSessionCashCountSchema = z9.object({
  quantities: cashCountQuantitiesSchema,
  stationName: z9.string().trim().max(CASH_COUNT_STATION_NAME_MAX).nullable().optional()
}).strict();
function emptyCashCountQuantities() {
  return Object.fromEntries(CASH_COUNT_DENOMINATIONS.map((denomination) => [String(denomination), 0]));
}
function computeCashCountTotal(quantities) {
  const agorot = CASH_COUNT_DENOMINATIONS.reduce(
    (sum, denomination) => sum + Math.round(denomination * 100) * (quantities[`${denomination}`] ?? 0),
    0
  );
  return agorot / 100;
}
function expectedCashFromReport(report) {
  return roundMoney(
    report.openingCash + report.drawers.reduce(
      (sum, drawer) => sum + drawer.cashTotalWithTips + drawer.cashInTotal - drawer.cashOutTotal,
      0
    )
  );
}
function cashInDrawerFromReport(report) {
  return roundMoney(expectedCashFromReport(report) - (report.excludeOpeningCashFromDrawer ?? true ? report.openingCash : 0));
}

// src/pos/tax-authority.model.ts
import { z as z10 } from "zod";
var TaxConnectionViewSchema = z10.object({
  tenantId: z10.string(),
  environment: z10.enum(["sandbox", "production"]).nullable(),
  status: z10.enum(["disabled", "disconnected", "pending", "connected", "reconnect-required"]),
  sellerVatNumber: z10.string().nullable(),
  softwareNumber: z10.string().nullable(),
  accessExpiresAt: z10.string().nullable(),
  refreshExpiresAt: z10.string().nullable(),
  updatedAt: z10.string()
}).strict();
var TaxConnectSchema = z10.object({
  sellerVatNumber: z10.string().regex(/^\d{9}$/)
}).strict();
var TaxInvoiceRequestSchema = z10.object({
  customerVatNumber: z10.string().regex(/^\d{9}$/),
  customerName: z10.string().trim().min(1).max(25),
  customerRequestedAllocation: z10.literal(true)
}).strict();
var TaxInvoiceViewSchema = z10.object({
  id: z10.string(),
  tenantId: z10.string(),
  orderId: z10.number().int().positive(),
  environment: z10.enum(["sandbox", "production"]),
  referenceNumber: z10.string(),
  status: z10.enum(["requesting", "approved", "refused", "not-approved", "failed", "unknown"]),
  allocationNumber: z10.string().nullable(),
  displayNumber: z10.string().nullable(),
  updatedAt: z10.string()
}).strict();

export {
  MONEY_DECIMALS,
  roundMoney,
  splitMoney,
  allocateMoneyPortion,
  updateRoundAmountsSchema,
  QUICK_CHARGE_CODE_PREFIX,
  KIOSK_DINE_IN_CODE_PREFIX,
  isQuickChargeOrderCode,
  isKioskDineInOrderCode,
  getComboMoveSource,
  isComboMoveNameMatch,
  hasPendingComboComponents,
  expandMergeTableIds,
  MIN_ORDER_COMPLETION_NOTE_PREFIX,
  MIN_ORDER_COMPLETION_LABEL,
  isMinOrderCompletionNote,
  resolveEffectiveMinimumOrder,
  CASH_ALTERATION_ACTIONS,
  cashAlterationActionSchema,
  FAILED_CREDIT_RECOVERY_DELAY_MS,
  buildJpegPdf,
  readJpegSize,
  allocatePaymentlineTips,
  resolveTip,
  allocateGlobalTipShare,
  allocateGlobalTip,
  getPrinterDisplayName,
  areaPrintRulePrinterMappingSchema,
  areaPrintRuleSchema,
  updateAreaPrintRulesSchema,
  POS_STREAM_EVENT_TYPES,
  zReportEmailRecipientsSchema,
  updateZReportEmailRecipientsSchema,
  zReportEmailPdfSchema,
  sendZReportEmailSchema,
  CLOSED_ORDERS_PAGE_SIZE_MAX,
  CLOSED_ORDERS_PAGE_SIZE_DEFAULT,
  SESSION_FILTER_OPTIONS_LIMIT,
  closedOrdersQuerySchema,
  accountantReportRangeSchema,
  accountantReportSessionsSchema,
  accountantReportDeliveryPatchSchema,
  buildAccountantReportHtml,
  DEFAULT_ATTENDANCE_REPORT_RULES,
  ASSISTANT_QUESTION_MIN_LENGTH,
  ASSISTANT_QUESTION_MAX_LENGTH,
  ASSISTANT_FEEDBACK_MIN_LENGTH,
  ASSISTANT_FEEDBACK_MAX_LENGTH,
  ASSISTANT_HISTORY_MAX_TURNS,
  ASSISTANT_HISTORY_TURN_MAX_LENGTH,
  zReportLayoutEntryTypeSchema,
  zReportLayoutEntrySchema,
  zReportLayoutConfigSchema,
  E2E_RUN_TIERS,
  E2E_RUN_BRANCH_PATTERN,
  e2eRunStartSchema,
  CASH_COUNT_BILLS,
  CASH_COUNT_COINS,
  CASH_COUNT_DENOMINATIONS,
  CASH_COUNT_MAX_QUANTITY,
  CASH_COUNT_STATION_NAME_MAX,
  cashCountQuantitiesSchema,
  upsertSessionCashCountSchema,
  emptyCashCountQuantities,
  computeCashCountTotal,
  expectedCashFromReport,
  cashInDrawerFromReport,
  TaxConnectionViewSchema,
  TaxConnectSchema,
  TaxInvoiceRequestSchema,
  TaxInvoiceViewSchema
};
